import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import API from "../config/api.js";

const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function AccountProfileImage() {
  const { user, refreshAuth } = useAuth();

  const [uploading, setUploading]   = useState(false);
  const [alert, setAlert]           = useState(null);
  const [preview, setPreview]       = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setAlert({ type: "danger", msg: "Please choose a JPG, PNG or WebP image." });
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setAlert({ type: "danger", msg: "Image must be smaller than 2MB." });
      return;
    }

    setAlert(null);
    setPreview(URL.createObjectURL(file));
    setUploading(true);

    try {
      const fd = new FormData();
      fd.append("image", file);

      const res = await fetch(`${API.account}?editProfileImage`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const ok = await res.json();

      if (ok === true) {
        setAlert({ type: "success", msg: "Profile photo updated!" });
        refreshAuth?.();
      } else {
        setPreview(null);
        setAlert({ type: "danger", msg: ok?.message || "Error uploading image." });
      }
    } catch (_) {
      setPreview(null);
      setAlert({ type: "danger", msg: "Error uploading image." });
    } finally {
      setUploading(false);
    }
  };

  const avatarSrc = preview || (user?.image ? API.baseUrl + '/' + user.image : null);
  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <div className="border rounded-4 p-4">
      <div className="row mb-3">
        <div className="col-md-7">
          <h2 className="fs-6 fw-700">Profile Photo</h2>
          <p className="text-muted">This photo will be displayed publicly across the community.</p>
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
          {alert.msg}
          <button type="button" className="btn-close" onClick={() => setAlert(null)} />
        </div>
      )}

      <div className="d-flex align-items-center gap-3">
        <div
          className="rounded-circle overflow-hidden border d-flex align-items-center justify-content-center bg-light position-relative"
          style={{ width: 88, height: 88, flexShrink: 0 }}
        >
          {avatarSrc ? (
            <img src={avatarSrc} alt="Profile" className="w-100 h-100" style={{ objectFit: "cover" }} />
          ) : (
            <span className="fs-3 fw-700 text-muted">{initial}</span>
          )}
          {uploading && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50">
              <div className="spinner-border spinner-border-sm text-white" role="status" />
            </div>
          )}
        </div>
        <div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="bi bi-camera me-1" /> {uploading ? "Uploading..." : "Change photo"}
          </button>
          <div className="text-muted small mt-1">JPG, PNG or WebP. Max 2MB.</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="d-none"
            onChange={handleImageChange}
          />
        </div>
      </div>
    </div>
  );
}